import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useStore } from '../store';
import apiService from '../services/api';
import { Booking } from '../types/booking';

const Bookings: React.FC = () => {
  const navigate = useNavigate();
  const bookings = useStore((state) => state.bookings);
  const setBookings = useStore((state) => state.setBookings);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const fetchBookings = async (showLoader = true) => {
    if (showLoader) setIsLoading(true);
    try {
      const data = await apiService.getBookings();
      setBookings(data);
    } catch (error) {
      // Error handled by API service
    } finally {
      setIsLoading(false);
      setIsRefreshing(false);
    }
  };

  useEffect(() => {
    fetchBookings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRefresh = () => {
    setIsRefreshing(true);
    fetchBookings(false);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (state: string) => {
    const statusMap: Record<string, string> = {
      draft: 'badge-warning',
      confirmed: 'badge-info',
      completed: 'badge-success',
      cancelled: 'badge-error',
    };
    return statusMap[state] || 'badge-ghost';
  };

  if (isLoading && bookings.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-[60vh]">
        <span className="loading loading-spinner loading-lg"></span>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">My Bookings</h1>
        <button
          onClick={handleRefresh}
          className={`btn btn-sm btn-ghost ${isRefreshing ? 'loading' : ''}`}
          disabled={isRefreshing}
        >
          {!isRefreshing && (
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
              />
            </svg>
          )}
        </button>
      </div>

      {bookings.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-base-content/60">No bookings found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {bookings.map((booking: Booking) => (
            
              <div
                key={booking.id}
                onClick={() => navigate(`/bookings/${booking.id}`)}
                className="card bg-base-100 shadow-sm hover:shadow-md transition-shadow cursor-pointer"
              >
                <div className="card-body p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <h3 className="font-semibold">{booking.subject || booking.name}</h3>
                      {booking.start_datetime && (
                        <p className="text-sm text-base-content/60">
                          {formatDate(booking.start_datetime)}
                        </p>
                      )}
                      {booking.partner_id && (
                        <p className="text-sm mt-1">
                          {booking.partner_id[1]}
                        </p>
                      )}
                      {booking.external_attendee_name && (
                        <p className="text-sm mt-1">
                          {booking.external_attendee_name}
                        </p>
                      )}
                    </div>
                    <div className={`badge ${getStatusBadge(booking.state)}`}>
                      {booking.state}
                    </div>
                  </div>
                </div>
              </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Bookings;